/**
 * The CRM API, as this app calls it.
 *
 * The port of `Services/HttpCrmService.cs`, which mirrors a 158-member `ICrmService` - so this file
 * grows one section per phase rather than arriving whole. Everything here is a thin call through
 * `request`, which already does the pre-emptive refresh, the one 401 replay, and the unwrapping of
 * the server's `{ error }` sentence into an `ApiError`.
 *
 * TWO DELIBERATE DIFFERENCES FROM THE C# CLIENT:
 *
 *   * A REFUSAL THROWS. `HttpCrmService` returns `(dto, error)` tuples because OpenSilver's command
 *     handlers had nowhere to catch from; here a page's action is an `async` function with a
 *     try/catch and a status line, so a thrown `ApiError` carrying the server's own sentence reaches
 *     the same place with less ceremony. `describeError` turns anything into something to show.
 *   * A LIST IS NEVER NULL. The C# client coalesces every list to an empty one, and so does this -
 *     a page that renders "no customers" for a failed load is the bug that shape prevents, so the
 *     coalescing happens only where the server genuinely answers with an empty list, and a transport
 *     failure still throws.
 */
import type {
  AttachmentDto,
  CreateStaffUserRequest,
  ResetStaffPasswordRequest,
  UpdateStaffUserRequest,
  UserDto,
  AddTicketNoteRequest,
  AdoptSnmpRequest,
  AdoptSnmpResponse,
  AgentDiagnosticDto,
  AgentVersionInfo,
  ApproveDeviceRequest,
  CatalogItemDto,
  CreateExpenseRequest,
  CreateRecurringExpenseRequest,
  ExpenseDto,
  ExpenseListDto,
  RecurringExpenseDto,
  AuditPageDto,
  LockedAccountDto,
  ProfitLossReportDto,
  SecurityAlertDto,
  TicketAlertDto,
  SignInFailureListDto,
  ReportSummaryDto,
  UpdateExpenseRequest,
  UpdateRecurringExpenseRequest,
  CreateCatalogItemRequest,
  CreateSoftwarePackageRequest,
  UpdateCatalogItemRequest,
  UpdateSoftwarePackageRequest,
  CatalogPriceQuote,
  CreateCustomerRequest,
  CreateLocalUserRequest,
  CreateLocalUserResult,
  DeleteLocalUserRequest,
  DeleteLocalUserResult,
  CreateEmployeeRequest,
  CreateTicketRequest,
  CustomerCatalogPriceDto,
  CustomerDto,
  DeviceDto,
  DeviceEventCategory,
  DeviceHistoryPage,
  DevicePresenceDto,
  DiscoveredDeviceDto,
  EmployeeDto,
  EnrollInfoDto,
  EventLogQueryRequest,
  EventLogQueryResult,
  FileBrowseResult,
  FileOperationCommand,
  FileOperationResult,
  InstallSoftwareResponse,
  LiveMetrics,
  LocalUserListResult,
  ResetLocalUserPasswordRequest,
  ResetLocalUserPasswordResult,
  SetLocalUserEnabledRequest,
  SetLocalUserEnabledResult,
  SetLocalUserGroupsRequest,
  SetLocalUserGroupsResult,
  DeviceServiceListResult,
  ServiceControlRequest,
  ServiceControlResult,
  SetServiceStartupTypeRequest,
  SetServiceStartupTypeResult,
  SetServiceRecoveryRequest,
  SetServiceRecoveryResult,
  SetServiceAccountRequest,
  SetServiceAccountResult,
  BulkServiceControlRequest,
  BulkServiceControlResult,
  AddInvoiceLineRequest,
  EmailInvoiceRequest,
  EmailInvoiceResult,
  InboundMailDto,
  InvoiceDto,
  InvoiceEmailDraftDto,
  InvoiceEmailDto,
  InvoiceListDto,
  InvoiceVersionDto,
  UpdateInvoiceLineRequest,
  UpdateInvoiceRequest,
  MetricsReport,
  MonitorTestResult,
  PollingAgentDto,
  PurgeCustomerResult,
  RecurringInvoiceDto,
  RecurringInvoiceRequest,
  RecurringInvoiceRunResult,
  RecurringRunResult,
  RecurringTicketDto,
  RecurringTicketRequest,
  RemoteTicketResponse,
  SnmpMonitorDto,
  SnmpTestResult,
  SoftwareInstallJobDto,
  SoftwareInventoryDto,
  SoftwarePackageDto,
  TestMonitorRequest,
  SnmpTestRequest,
  TicketDto,
  UpdateCustomerRequest,
  UpdateTicketRequest,
  WakeDeviceResponse,
  UpdateEmployeeRequest,
  AppSettingsDto,
  AppSettingsResponse,
  MailAccountDto,
  MailTestRequest,
  MailTestResult,
  UpdateMailAccountRequest,
  InvoicePaymentsDto,
  PayPalAccountDto,
  PayPalTestRequest,
  PayPalTestResult,
  UpdatePayPalAccountRequest,
  ServerUpdateActionResult,
  ServerUpdateConfigDto,
  ServerUpdateStatusDto,
  UpdateServerUpdateConfigRequest,
  AutoBillingDto,
  AutoBillRunResult,
  BillingConfigDto,
  CreatePurchaseRequest,
  CreateTimeEntryRequest,
  PurchaseDto,
  TimeEntryDto,
  UpdatePurchaseRequest,
  UpdateTimeEntryRequest,
  WorkTimerDto,
  DeployLogDto,
  EnrollSynologyRequest,
  SynologyTestRequest,
  SynologyTestResult,
  UpdateSnmpMonitorRequest
} from '../contracts.gen.js';
import {
  AgentPayloads,
  type CatalogItemKind,
  type ExpenseCategory,
  type AuditActorKind,
  type AuditCategory,
  type AuditOutcome,
  type InvoiceStatus,
  type ProfitLossGrouping
} from '../contracts.gen.js';
import { API_BASE, ApiError } from './auth.js';
import { request } from './http.js';

// ---------------------------------------------------------------------
//  Customers
// ---------------------------------------------------------------------

export function getCustomers(signal?: AbortSignal): Promise<CustomerDto[]> {
  return request<CustomerDto[]>('/api/customers', { signal });
}

export function getArchivedCustomers(signal?: AbortSignal): Promise<CustomerDto[]> {
  return request<CustomerDto[]>('/api/customers/archived', { signal });
}

/** Reads an archived customer too - which is how Restore is reached, since it lives on that page. */
export function getCustomer(id: number, signal?: AbortSignal): Promise<CustomerDto> {
  return request<CustomerDto>(`/api/customers/${id}`, { signal });
}

export function createCustomer(req: CreateCustomerRequest): Promise<CustomerDto> {
  return request<CustomerDto>('/api/customers', { method: 'POST', body: req });
}

export function updateCustomer(id: number, req: UpdateCustomerRequest): Promise<CustomerDto> {
  return request<CustomerDto>(`/api/customers/${id}`, { method: 'PUT', body: req });
}

/**
 * Archives. Named for what it does rather than for the verb on the route: `DELETE /api/customers/12`
 * destroys nothing, and calling this `deleteCustomer` is how a caller comes to believe it does.
 */
export function archiveCustomer(id: number): Promise<CustomerDto> {
  return request<CustomerDto>(`/api/customers/${id}`, { method: 'DELETE' });
}

export function restoreCustomer(id: number): Promise<CustomerDto> {
  return request<CustomerDto>(`/api/customers/${id}/unarchive`, { method: 'POST', body: {} });
}

/**
 * Destroys an archived customer for good.
 *
 * A POST rather than a DELETE because the typed confirmation travels in the body: a body on DELETE
 * is legal but unreliable through the browser fetch stack, and a customer's name has no business in
 * a URL that reaches an access log.
 */
export function purgeCustomer(id: number, confirmName: string): Promise<PurgeCustomerResult> {
  return request<PurgeCustomerResult>(`/api/customers/${id}/purge`, {
    method: 'POST',
    body: { confirmName }
  });
}

// ---------------------------------------------------------------------
//  Employees
// ---------------------------------------------------------------------

export function getEmployees(customerId: number, signal?: AbortSignal): Promise<EmployeeDto[]> {
  return request<EmployeeDto[]>(`/api/customers/${customerId}/employees`, { signal });
}

export function createEmployee(req: CreateEmployeeRequest): Promise<EmployeeDto> {
  return request<EmployeeDto>('/api/employees', { method: 'POST', body: req });
}

export function updateEmployee(id: number, req: UpdateEmployeeRequest): Promise<EmployeeDto> {
  return request<EmployeeDto>(`/api/employees/${id}`, { method: 'PUT', body: req });
}

export function deleteEmployee(id: number): Promise<void> {
  return request<void>(`/api/employees/${id}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------
//  Devices, as far as a customer page needs them
// ---------------------------------------------------------------------

export function getDevices(customerId?: number, signal?: AbortSignal): Promise<DeviceDto[]> {
  const url = customerId === undefined ? '/api/devices' : `/api/devices?customerId=${customerId}`;
  return request<DeviceDto[]>(url, { signal });
}

export function getPendingDevices(customerId?: number, signal?: AbortSignal): Promise<DeviceDto[]> {
  const url =
    customerId === undefined ? '/api/devices/pending' : `/api/devices/pending?customerId=${customerId}`;
  return request<DeviceDto[]>(url, { signal });
}

/**
 * Approves an enrollment, assigning its people in the same call.
 *
 * One call and not two because those are one decision: a device approved and then left unassigned is
 * a machine nobody is responsible for, and the server takes both together for exactly that reason.
 */
export function approveDevice(
  deviceId: number,
  employeeIds: number[],
  primaryEmployeeId: number | null
): Promise<DeviceDto> {
  const body: ApproveDeviceRequest = { employeeIds, primaryEmployeeId };
  return request<DeviceDto>(`/api/devices/${deviceId}/approve`, { method: 'POST', body });
}

export function rejectDevice(deviceId: number): Promise<void> {
  return request<void>(`/api/devices/${deviceId}/reject`, { method: 'POST', body: {} });
}

// ---------------------------------------------------------------------
//  Monitors, tickets, invoices
// ---------------------------------------------------------------------

export function getCustomerMonitors(customerId: number, signal?: AbortSignal): Promise<SnmpMonitorDto[]> {
  return request<SnmpMonitorDto[]>(`/api/customers/${customerId}/monitors`, { signal });
}

export function getTickets(customerId?: number, signal?: AbortSignal): Promise<TicketDto[]> {
  const url = customerId === undefined ? '/api/tickets' : `/api/tickets?customerId=${customerId}`;
  return request<TicketDto[]>(url, { signal });
}

export function createTicket(req: CreateTicketRequest): Promise<TicketDto> {
  return request<TicketDto>('/api/tickets', { method: 'POST', body: req });
}

/** Who is emailed when a ticket is raised from the agent tray. */
// ---- Attachments ----

/** A ticket's files, or with `ticketId` undefined the files on the customer itself. */
export function getAttachments(owner: { customerId?: number; ticketId?: number }, signal?: AbortSignal): Promise<AttachmentDto[]> {
  const query = owner.ticketId !== undefined ? `ticketId=${owner.ticketId}` : `customerId=${owner.customerId}`;
  return request<AttachmentDto[]>(`/api/attachments?${query}`, { signal });
}

/** Where `uploadFiles` posts; the answer is the rows added. */
export function attachmentUploadPath(owner: { customerId?: number; ticketId?: number }): string {
  return owner.ticketId !== undefined
    ? `/api/attachments/ticket/${owner.ticketId}`
    : `/api/attachments/customer/${owner.customerId}`;
}

/** The bytes: inline with a browser-displayable type when `view`, a download under the original name otherwise. */
export function attachmentPath(id: number, view = false): string {
  return view ? `/api/attachments/${id}?view=true` : `/api/attachments/${id}`;
}

export function deleteAttachment(id: number): Promise<void> {
  return request<void>(`/api/attachments/${id}`, { method: 'DELETE' });
}

export function getTicketAlerts(signal?: AbortSignal): Promise<TicketAlertDto> {
  return request<TicketAlertDto>('/api/tickets/alerts', { signal });
}

/** Blank switches the emails off; a malformed address is refused with a sentence. Admin only. */
export function saveTicketAlerts(notifyEmail: string): Promise<TicketAlertDto> {
  return request<TicketAlertDto>('/api/tickets/alerts', { method: 'PUT', body: { notifyEmail } });
}

export function getTicket(id: number, signal?: AbortSignal): Promise<TicketDto> {
  return request<TicketDto>(`/api/tickets/${id}`, { signal });
}

/** The server writes a status change as an attributed internal note, so the answer carries it. */
export function updateTicket(id: number, req: UpdateTicketRequest): Promise<TicketDto> {
  return request<TicketDto>(`/api/tickets/${id}`, { method: 'PUT', body: req });
}

export function addTicketNote(id: number, req: AddTicketNoteRequest): Promise<TicketDto> {
  return request<TicketDto>(`/api/tickets/${id}/notes`, { method: 'POST', body: req });
}

export function getCustomerInvoices(customerId: number, signal?: AbortSignal): Promise<InvoiceDto[]> {
  return request<InvoiceDto[]>(`/api/customers/${customerId}/invoices`, { signal });
}

/**
 * Invoices whose customer was permanently deleted.
 *
 * They exist because an issued or paid invoice records money actually billed, and the archived list
 * is the only place they are reachable - so a page that does not show them makes them unreachable.
 */
export function getOrphanedInvoices(signal?: AbortSignal): Promise<InvoiceDto[]> {
  return request<InvoiceDto[]>('/api/invoices/orphaned', { signal });
}

// ---------------------------------------------------------------------
//  Invoices - the cross-customer list, one invoice, its lifecycle and its mail
// ---------------------------------------------------------------------

export interface InvoiceFilter {
  status?: InvoiceStatus;
  /** Issued and past due. Beats `status` server-side when both are given. */
  overdue?: boolean;
  customerId?: number;
  /** Creation bounds as instants; `to` is exclusive. */
  fromUtc?: string;
  toUtc?: string;
  search?: string;
}

/** Every invoice the filter allows, plus a receivables summary that ignores the filter. */
export function getAllInvoices(filter: InvoiceFilter, signal?: AbortSignal): Promise<InvoiceListDto> {
  const q = new URLSearchParams();
  if (filter.status !== undefined) q.set('status', String(filter.status));
  if (filter.overdue) q.set('overdue', 'true');
  if (filter.customerId !== undefined) q.set('customerId', String(filter.customerId));
  if (filter.fromUtc) q.set('from', filter.fromUtc);
  if (filter.toUtc) q.set('to', filter.toUtc);
  if (filter.search?.trim()) q.set('search', filter.search.trim());
  const query = q.toString();
  return request<InvoiceListDto>('/api/invoices' + (query ? '?' + query : ''), { signal });
}

export function getInvoice(id: number, signal?: AbortSignal): Promise<InvoiceDto> {
  return request<InvoiceDto>(`/api/invoices/${id}`, { signal });
}

/** `dueUtc` null keeps whatever due date the invoice already has (or its terms). */
export function issueInvoice(id: number, dueUtc: string | null = null): Promise<InvoiceDto> {
  return request<InvoiceDto>(`/api/invoices/${id}/issue`, { method: 'POST', body: { dueUtc } });
}

/** Paid today unless a date is given - the server stamps it. */
export function markInvoicePaid(id: number, paidUtc: string | null = null): Promise<InvoiceDto> {
  return request<InvoiceDto>(`/api/invoices/${id}/paid`, { method: 'POST', body: { paidUtc } });
}

export function voidInvoice(id: number): Promise<InvoiceDto> {
  return request<InvoiceDto>(`/api/invoices/${id}/void`, { method: 'POST', body: {} });
}

export function updateInvoice(id: number, req: UpdateInvoiceRequest): Promise<InvoiceDto> {
  return request<InvoiceDto>(`/api/invoices/${id}`, { method: 'PUT', body: req });
}

export function addInvoiceLine(id: number, req: AddInvoiceLineRequest): Promise<InvoiceDto> {
  return request<InvoiceDto>(`/api/invoices/${id}/lines`, { method: 'POST', body: req });
}

export function updateInvoiceLine(id: number, lineId: number, req: UpdateInvoiceLineRequest): Promise<InvoiceDto> {
  return request<InvoiceDto>(`/api/invoices/${id}/lines/${lineId}`, { method: 'PUT', body: req });
}

export function removeInvoiceLine(id: number, lineId: number): Promise<InvoiceDto> {
  return request<InvoiceDto>(`/api/invoices/${id}/lines/${lineId}`, { method: 'DELETE' });
}

export function getInvoiceHistory(id: number, signal?: AbortSignal): Promise<InvoiceVersionDto[]> {
  return request<InvoiceVersionDto[]>(`/api/invoices/${id}/history`, { signal });
}

export function restoreInvoiceVersion(id: number, version: number): Promise<InvoiceDto> {
  return request<InvoiceDto>(`/api/invoices/${id}/restore/${version}`, { method: 'POST', body: {} });
}

export function invoicePdfPath(id: number): string {
  return `/api/invoices/${id}/pdf`;
}

/** The suggested recipients, and the subject and body with the templates already filled in. */
export function getInvoiceEmailDraft(id: number, signal?: AbortSignal): Promise<InvoiceEmailDraftDto> {
  return request<InvoiceEmailDraftDto>(`/api/invoices/${id}/email`, { signal });
}

/**
 * Sends the invoice. A refusal by the mail server is a 200 with `success: false` and a sentence -
 * an outcome, not an error - so only a transport failure throws.
 */
export function emailInvoice(id: number, req: EmailInvoiceRequest): Promise<EmailInvoiceResult> {
  return request<EmailInvoiceResult>(`/api/invoices/${id}/email`, { method: 'POST', body: req });
}

export function getInvoiceEmailHistory(id: number, signal?: AbortSignal): Promise<InvoiceEmailDto[]> {
  return request<InvoiceEmailDto[]>(`/api/invoices/${id}/email-history`, { signal });
}

/** The customer's pay link, whether PayPal would take it now, and every online payment attempt. */
export function getInvoicePayments(id: number, signal?: AbortSignal): Promise<InvoicePaymentsDto> {
  return request<InvoicePaymentsDto>(`/api/invoices/${id}/payments`, { signal });
}

export function getInvoiceCorrespondence(id: number, signal?: AbortSignal): Promise<InboundMailDto[]> {
  return request<InboundMailDto[]>(`/api/invoices/${id}/correspondence`, { signal });
}

// ---------------------------------------------------------------------
//  Catalog and contract pricing
// ---------------------------------------------------------------------

export function getCatalog(includeInactive = false, signal?: AbortSignal): Promise<CatalogItemDto[]> {
  return request<CatalogItemDto[]>(`/api/catalog?includeInactive=${includeInactive}`, { signal });
}

export interface CatalogFilter {
  search?: string;
  kind?: CatalogItemKind;
  includeInactive?: boolean;
}

/** The price list as the Catalog page filters it. `getCatalog` is the pickers' plain read. */
export function searchCatalog(filter: CatalogFilter, signal?: AbortSignal): Promise<CatalogItemDto[]> {
  const q = new URLSearchParams({ includeInactive: String(filter.includeInactive ?? false) });
  if (filter.search?.trim()) q.set('search', filter.search.trim());
  if (filter.kind !== undefined) q.set('kind', String(filter.kind));
  return request<CatalogItemDto[]>(`/api/catalog?${q.toString()}`, { signal });
}

export function createCatalogItem(req: CreateCatalogItemRequest): Promise<CatalogItemDto> {
  return request<CatalogItemDto>('/api/catalog', { method: 'POST', body: req });
}

export function updateCatalogItem(id: number, req: UpdateCatalogItemRequest): Promise<CatalogItemDto> {
  return request<CatalogItemDto>(`/api/catalog/${id}`, { method: 'PUT', body: req });
}

/**
 * Removes an item. Resolves to null when it was really deleted (204), or to the server's sentence
 * when it was RETIRED instead because charges reference it (200 `{ error }`) - not a failure, but
 * one the caller has to show, since the row stays in an "include retired" list.
 */
export async function deleteCatalogItem(id: number): Promise<string | null> {
  const body = await request<{ error?: string } | undefined>(`/api/catalog/${id}`, { method: 'DELETE' });
  return body?.error ?? null;
}

export function getContractPrices(
  customerId: number,
  signal?: AbortSignal
): Promise<CustomerCatalogPriceDto[]> {
  return request<CustomerCatalogPriceDto[]>(`/api/customers/${customerId}/catalog-prices`, { signal });
}

export function setContractPrice(
  customerId: number,
  itemId: number,
  unitPrice: number
): Promise<CustomerCatalogPriceDto> {
  return request<CustomerCatalogPriceDto>(`/api/customers/${customerId}/catalog-prices/${itemId}`, {
    method: 'PUT',
    body: { unitPrice }
  });
}

export function clearContractPrice(customerId: number, itemId: number): Promise<void> {
  return request<void>(`/api/customers/${customerId}/catalog-prices/${itemId}`, { method: 'DELETE' });
}

/**
 * What this customer pays for a catalog item, and which of the three rules said so.
 *
 * The rules - a contract price, then the item's list price, then cost plus this customer's markup -
 * are decided in exactly one place on the server, and the explanation travels with the number
 * because "why is this $99?" is the question a price list actually gets asked.
 */
export function quoteCatalogItem(
  customerId: number,
  itemId: number,
  signal?: AbortSignal
): Promise<CatalogPriceQuote> {
  return request<CatalogPriceQuote>(`/api/customers/${customerId}/catalog/${itemId}/quote`, { signal });
}

// ---------------------------------------------------------------------
//  Enrollment
// ---------------------------------------------------------------------

export function getEnrollmentInstaller(customerId: number): Promise<EnrollInfoDto> {
  return request<EnrollInfoDto>(`/api/customers/${customerId}/enrollment-installer`);
}

/** The `.cmd` installer, which downloads the agent when it runs. Fetched, never linked: it needs the token. */
export function installerUrl(customerId: number): string {
  return `${API_BASE}/api/customers/${customerId}/installer`;
}

/** The whole agent in a zip, for a machine with no route to this server until it has enrolled. */
export function installerBundleUrl(customerId: number): string {
  return `${API_BASE}/api/customers/${customerId}/installer-bundle`;
}

// ---------------------------------------------------------------------
//  Agent diagnostics
// ---------------------------------------------------------------------

/**
 * `--diagnose` reports from machines that installed and never enrolled.
 *
 * Read on the customer page beside the approval queue, because a machine that failed to enroll never
 * reaches Pending: together the two answer the whole of "this PC should be here and is not".
 */
export function getAgentDiagnostics(
  customerId: number,
  signal?: AbortSignal
): Promise<AgentDiagnosticDto[]> {
  return request<AgentDiagnosticDto[]>(`/api/customers/${customerId}/agent-diagnostics`, { signal });
}

// ---------------------------------------------------------------------
//  Recurring invoices and recurring tickets
// ---------------------------------------------------------------------

export function getRecurringInvoices(signal?: AbortSignal): Promise<RecurringInvoiceDto[]> {
  return request<RecurringInvoiceDto[]>('/api/recurring-invoices', { signal });
}

export function createRecurringInvoice(req: RecurringInvoiceRequest): Promise<RecurringInvoiceDto> {
  return request<RecurringInvoiceDto>('/api/recurring-invoices', { method: 'POST', body: req });
}

export function updateRecurringInvoice(
  id: number,
  req: RecurringInvoiceRequest
): Promise<RecurringInvoiceDto> {
  return request<RecurringInvoiceDto>(`/api/recurring-invoices/${id}`, { method: 'PUT', body: req });
}

export function deleteRecurringInvoice(id: number): Promise<void> {
  return request<void>(`/api/recurring-invoices/${id}`, { method: 'DELETE' });
}

export function runRecurringInvoices(): Promise<RecurringInvoiceRunResult> {
  return request<RecurringInvoiceRunResult>('/api/recurring-invoices/run', { method: 'POST', body: {} });
}

export function getRecurringTickets(signal?: AbortSignal): Promise<RecurringTicketDto[]> {
  return request<RecurringTicketDto[]>('/api/recurring-tickets', { signal });
}

export function createRecurringTicket(req: RecurringTicketRequest): Promise<RecurringTicketDto> {
  return request<RecurringTicketDto>('/api/recurring-tickets', { method: 'POST', body: req });
}

export function updateRecurringTicket(
  id: number,
  req: RecurringTicketRequest
): Promise<RecurringTicketDto> {
  return request<RecurringTicketDto>(`/api/recurring-tickets/${id}`, { method: 'PUT', body: req });
}

export function deleteRecurringTicket(id: number): Promise<void> {
  return request<void>(`/api/recurring-tickets/${id}`, { method: 'DELETE' });
}

export function runRecurringTickets(): Promise<RecurringRunResult> {
  return request<RecurringRunResult>('/api/recurring-tickets/run', { method: 'POST', body: {} });
}

// ---------------------------------------------------------------------
//  Devices: the fleet, its lifecycle, and everything done to one machine
// ---------------------------------------------------------------------

export function getDevice(id: number, signal?: AbortSignal): Promise<DeviceDto> {
  return request<DeviceDto>(`/api/devices/${id}`, { signal });
}

export function getArchivedDevices(customerId?: number, signal?: AbortSignal): Promise<DeviceDto[]> {
  const url =
    customerId === undefined ? '/api/devices/archived' : `/api/devices/archived?customerId=${customerId}`;
  return request<DeviceDto[]>(url, { signal });
}

/**
 * Presence for the whole fleet, in one read.
 *
 * Polled every four seconds by the devices list. Deliberately one call rather than one per row: the
 * server holds presence in memory, so the whole fleet is cheaper than the round trips.
 */
export function getAllPresence(signal?: AbortSignal): Promise<DevicePresenceDto[]> {
  return request<DevicePresenceDto[]>('/api/devices/presence', { signal });
}

export function getAllMonitors(signal?: AbortSignal): Promise<SnmpMonitorDto[]> {
  return request<SnmpMonitorDto[]>('/api/monitors', { signal });
}

/** The SNMP monitor watching this same machine, when one exists. Null when none does. */
export async function findDeviceMonitor(
  deviceId: number,
  signal?: AbortSignal
): Promise<SnmpMonitorDto | null> {
  try {
    return await request<SnmpMonitorDto>(`/api/devices/${deviceId}/monitor`, { signal });
  } catch (error) {
    // A device with no monitor is the ordinary case and answers 404; anything else is worth raising.
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

export function archiveDevice(deviceId: number, archived: boolean): Promise<DeviceDto> {
  return request<DeviceDto>(`/api/devices/${deviceId}/${archived ? 'archive' : 'unarchive'}`, {
    method: 'POST',
    body: {}
  });
}

/**
 * Decommissions, or - for an already-archived device - destroys the record.
 *
 * The server decides which from the device's own state, so this is one call with one meaning per
 * state, exactly as `DevicesController.Delete` reads it. `force` overrides only the pending-uninstall
 * guard, for a machine that is gone and will never acknowledge: it does NOT skip the decommission.
 */
export function deleteDevice(deviceId: number, force = false): Promise<DeviceDto | void> {
  return request<DeviceDto | void>(`/api/devices/${deviceId}${force ? '?force=true' : ''}`, {
    method: 'DELETE'
  });
}

export function setMaintenanceMode(deviceId: number, enabled: boolean): Promise<DeviceDto> {
  return request<DeviceDto>(`/api/devices/${deviceId}/maintenance`, {
    method: 'POST',
    body: { enabled }
  });
}

/** The persisted 24-hour window a device collects while maintenance mode is on. */
export function getMaintenanceHistory(deviceId: number, signal?: AbortSignal): Promise<LiveMetrics[]> {
  return request<LiveMetrics[]>(`/api/devices/${deviceId}/maintenance-history`, { signal });
}

export function restartDevice(deviceId: number): Promise<void> {
  return request<void>(`/api/devices/${deviceId}/restart`, { method: 'POST', body: {} });
}

/**
 * Sends a magic packet from another of the customer's agents.
 *
 * SUCCESS MEANS THE PACKET WENT OUT, never that the machine woke - Wake-on-LAN is unacknowledged at
 * every layer, so the response's own wording says "sent" and the device coming online is the proof.
 * `viaDeviceId` overrides the server's pick, which otherwise ranks the customer's online agents by
 * shared subnet and then shared site.
 */
export function wakeDevice(deviceId: number, viaDeviceId?: number): Promise<WakeDeviceResponse> {
  return request<WakeDeviceResponse>(`/api/devices/${deviceId}/wake`, {
    method: 'POST',
    body: { viaDeviceId: viaDeviceId ?? null }
  });
}

/** The customer's agents that could poll or wake something - with their online state. */
export function getPollingAgents(customerId: number, signal?: AbortSignal): Promise<PollingAgentDto[]> {
  return request<PollingAgentDto[]>(`/api/customers/${customerId}/polling-agents`, { signal });
}

// ---- Agent version and updates ----

/**
 * The version the server is serving for one payload flavor.
 *
 * Per-flavor because a Synology agent must be measured against the Synology payload and not the
 * Windows one; the flavorless call is kept for Windows because an older server answers only that.
 */
export async function getLatestAgentVersion(
  payload?: string,
  signal?: AbortSignal
): Promise<string | null> {
  const url =
    payload && payload !== AgentPayloads.Windows
      ? `/api/devices/agent-version?payload=${encodeURIComponent(payload)}`
      : '/api/devices/agent-version';
  const info = await request<AgentVersionInfo>(url, { signal });
  return info?.version ?? null;
}

export async function pushAgentUpdate(): Promise<string | null> {
  const info = await request<AgentVersionInfo>('/api/devices/push-agent-update', {
    method: 'POST',
    body: {}
  });
  return info?.version ?? null;
}

export async function pushDeviceAgentUpdate(deviceId: number): Promise<string | null> {
  const info = await request<AgentVersionInfo>(`/api/devices/${deviceId}/push-agent-update`, {
    method: 'POST',
    body: {}
  });
  return info?.version ?? null;
}

// ---- Software inventory, patches and updates ----

export function getSoftwareInventory(
  deviceId: number,
  signal?: AbortSignal
): Promise<SoftwareInventoryDto | null> {
  return request<SoftwareInventoryDto | null>(`/api/devices/${deviceId}/software`, { signal });
}

export function refreshSoftwareInventory(deviceId: number): Promise<void> {
  return request<void>(`/api/devices/${deviceId}/software/refresh`, { method: 'POST', body: {} });
}

/**
 * Uninstalls by INVENTORY KEY, never by command.
 *
 * The key is a registry view plus a subkey name; the agent re-opens it in its own registry, checks
 * the display name still matches, and runs what IT finds there. So a confused server can pick what
 * is uninstalled but never what runs - which is why this takes a key and not a string to execute.
 */
export function uninstallSoftware(deviceId: number, key: string): Promise<void> {
  return request<void>(`/api/devices/${deviceId}/software/uninstall`, { method: 'POST', body: { key } });
}

export function uninstallPatch(deviceId: number, hotFixId: string): Promise<void> {
  return request<void>(`/api/devices/${deviceId}/patches/uninstall`, {
    method: 'POST',
    body: { hotFixId }
  });
}

/** One update by its WUA id, or every pending one when `updateId` is omitted. */
export function installUpdates(deviceId: number, updateId?: string): Promise<void> {
  return request<void>(`/api/devices/${deviceId}/updates/install`, {
    method: 'POST',
    body: { updateId: updateId ?? null }
  });
}

// ---- Remote software installation ----

export function getSoftwarePackages(
  includeRetired = false,
  signal?: AbortSignal,
  search = ''
): Promise<SoftwarePackageDto[]> {
  const q = new URLSearchParams();
  if (includeRetired) q.set('includeRetired', 'true');
  if (search.trim()) q.set('search', search.trim());
  const query = q.toString();
  return request<SoftwarePackageDto[]>('/api/software-packages' + (query ? '?' + query : ''), { signal });
}

/** Admin only: a package is a command line that runs as SYSTEM wherever it is sent. */
export function createSoftwarePackage(req: CreateSoftwarePackageRequest): Promise<SoftwarePackageDto> {
  return request<SoftwarePackageDto>('/api/software-packages', { method: 'POST', body: req });
}

export function updateSoftwarePackage(id: number, req: UpdateSoftwarePackageRequest): Promise<SoftwarePackageDto> {
  return request<SoftwarePackageDto>(`/api/software-packages/${id}`, { method: 'PUT', body: req });
}

/**
 * Removes a package. `removed` false means it was RETIRED instead, because devices have installed it
 * and their jobs must keep saying what they ran - `message` is the server's sentence for that.
 */
export async function deleteSoftwarePackage(id: number): Promise<{ removed: boolean; message: string }> {
  const body = await request<{ retired?: boolean; message?: string } | undefined>(`/api/software-packages/${id}`, {
    method: 'DELETE'
  });
  return body?.retired
    ? { removed: false, message: body.message ?? 'The package was retired rather than deleted.' }
    : { removed: true, message: '' };
}

export function softwarePackageFilePath(id: number): string {
  return `/api/software-packages/${id}/file`;
}

export function installSoftware(
  packageId: number,
  deviceIds: number[]
): Promise<InstallSoftwareResponse> {
  return request<InstallSoftwareResponse>(`/api/software-packages/${packageId}/install`, {
    method: 'POST',
    body: { deviceIds }
  });
}

export function getSoftwareInstalls(
  deviceId: number,
  limit = 0,
  signal?: AbortSignal
): Promise<SoftwareInstallJobDto[]> {
  const url = `/api/devices/${deviceId}/software/installs${limit > 0 ? `?limit=${limit}` : ''}`;
  return request<SoftwareInstallJobDto[]>(url, { signal });
}

// ---- Remote file explorer ----

/**
 * Lists a folder on the device.
 *
 * A request/response over the agent hub as a SignalR client result, so from here it is a plain REST
 * round trip - and an unreachable device is a clean 409 rather than a hang. The answer carries its
 * own `success`/`error`, because a refused browse is an outcome and not a transport failure.
 */
export function browseDeviceFiles(
  deviceId: number,
  path: string | null,
  signal?: AbortSignal
): Promise<FileBrowseResult> {
  return request<FileBrowseResult>(`/api/devices/${deviceId}/files/browse`, {
    method: 'POST',
    body: { path },
    ...(signal ? { signal } : {})
  });
}

export function deviceFileOperation(
  deviceId: number,
  command: FileOperationCommand
): Promise<FileOperationResult> {
  return request<FileOperationResult>(`/api/devices/${deviceId}/files/operation`, {
    method: 'POST',
    body: command
  });
}

/** The authenticated path a file's bytes come from. Fetched, never linked - see `download.ts`. */
export function deviceFileDownloadPath(deviceId: number, path: string): string {
  return `/api/devices/${deviceId}/files/download?path=${encodeURIComponent(path)}`;
}

export function deviceFileUploadPath(deviceId: number, targetDirectory: string): string {
  return `/api/devices/${deviceId}/files/upload?path=${encodeURIComponent(targetDirectory)}`;
}

// ---- Event log ----

export function queryDeviceEventLog(
  deviceId: number,
  req: EventLogQueryRequest,
  signal?: AbortSignal
): Promise<EventLogQueryResult> {
  return request<EventLogQueryResult>(`/api/devices/${deviceId}/eventlog/query`, {
    method: 'POST',
    body: req,
    ...(signal ? { signal } : {})
  });
}

// ---- Local user accounts ----

/** The device's local accounts, read live. A refusal from the device is a result, not a throw. */
export function listDeviceUsers(deviceId: number, signal?: AbortSignal): Promise<LocalUserListResult> {
  return request<LocalUserListResult>(`/api/devices/${deviceId}/users`, { ...(signal ? { signal } : {}) });
}

/**
 * Creates one local account. The password travels in this body and nowhere else; the device's
 * refusal (its password policy, a name already taken) comes back as `success: false`, not a throw.
 */
export function createDeviceUser(
  deviceId: number,
  req: CreateLocalUserRequest,
  signal?: AbortSignal
): Promise<CreateLocalUserResult> {
  return request<CreateLocalUserResult>(`/api/devices/${deviceId}/users`, {
    method: 'POST',
    body: req,
    ...(signal ? { signal } : {})
  });
}

/**
 * Sets a new password on an existing local account. The account name rides in the body beside the
 * password, never in the URL; a device refusal is `success: false`, not a throw.
 */
export function resetDeviceUserPassword(
  deviceId: number,
  req: ResetLocalUserPasswordRequest,
  signal?: AbortSignal
): Promise<ResetLocalUserPasswordResult> {
  return request<ResetLocalUserPasswordResult>(`/api/devices/${deviceId}/users/password`, {
    method: 'POST',
    body: req,
    ...(signal ? { signal } : {})
  });
}

/**
 * Deletes a local account, optionally with its profile. A POST with the typed confirmation in the
 * body, never a DELETE - see the controller.
 */
export function deleteDeviceUser(
  deviceId: number,
  req: DeleteLocalUserRequest,
  signal?: AbortSignal
): Promise<DeleteLocalUserResult> {
  return request<DeleteLocalUserResult>(`/api/devices/${deviceId}/users/delete`, {
    method: 'POST',
    body: req,
    ...(signal ? { signal } : {})
  });
}

/** Disables or re-enables a local account; a device refusal is `success: false`, not a throw. */
export function setDeviceUserEnabled(
  deviceId: number,
  req: SetLocalUserEnabledRequest,
  signal?: AbortSignal
): Promise<SetLocalUserEnabledResult> {
  return request<SetLocalUserEnabledResult>(`/api/devices/${deviceId}/users/enabled`, {
    method: 'POST',
    body: req,
    ...(signal ? { signal } : {})
  });
}

/** Puts a local account in or out of Administrators and Remote Desktop Users; a device refusal is `success: false`. */
export function setDeviceUserGroups(
  deviceId: number,
  req: SetLocalUserGroupsRequest,
  signal?: AbortSignal
): Promise<SetLocalUserGroupsResult> {
  return request<SetLocalUserGroupsResult>(`/api/devices/${deviceId}/users/groups`, {
    method: 'POST',
    body: req,
    ...(signal ? { signal } : {})
  });
}

// ---- Windows services ----

export function listDeviceServices(deviceId: number, signal?: AbortSignal): Promise<DeviceServiceListResult> {
  return request<DeviceServiceListResult>(`/api/devices/${deviceId}/services`, { ...(signal ? { signal } : {}) });
}

export function controlDeviceService(
  deviceId: number,
  req: ServiceControlRequest,
  signal?: AbortSignal
): Promise<ServiceControlResult> {
  return request<ServiceControlResult>(`/api/devices/${deviceId}/services/control`, {
    method: 'POST',
    body: req,
    ...(signal ? { signal } : {})
  });
}

export function setDeviceServiceStartupType(
  deviceId: number,
  req: SetServiceStartupTypeRequest,
  signal?: AbortSignal
): Promise<SetServiceStartupTypeResult> {
  return request<SetServiceStartupTypeResult>(`/api/devices/${deviceId}/services/startup`, {
    method: 'POST',
    body: req,
    ...(signal ? { signal } : {})
  });
}

export function setDeviceServiceRecovery(
  deviceId: number,
  req: SetServiceRecoveryRequest,
  signal?: AbortSignal
): Promise<SetServiceRecoveryResult> {
  return request<SetServiceRecoveryResult>(`/api/devices/${deviceId}/services/recovery`, {
    method: 'POST',
    body: req,
    ...(signal ? { signal } : {})
  });
}

/** Admin-only on the server. The password travels in this body and nowhere else. */
export function setDeviceServiceAccount(
  deviceId: number,
  req: SetServiceAccountRequest,
  signal?: AbortSignal
): Promise<SetServiceAccountResult> {
  return request<SetServiceAccountResult>(`/api/devices/${deviceId}/services/account`, {
    method: 'POST',
    body: req,
    ...(signal ? { signal } : {})
  });
}

export function controlServiceOnDevices(req: BulkServiceControlRequest, signal?: AbortSignal): Promise<BulkServiceControlResult> {
  return request<BulkServiceControlResult>('/api/services/control-many', {
    method: 'POST',
    body: req,
    ...(signal ? { signal } : {})
  });
}

// ---- Device history ----

export interface DeviceHistoryQuery {
  limit?: number;
  category?: DeviceEventCategory;
  /**
   * The oldest row on screen.
   *
   * Paging walks backwards from an id rather than by offset, because entries keep arriving while a
   * technician reads and an offset would slide the page under them.
   */
  beforeId?: number;
  search?: string;
}

export function getDeviceHistory(
  deviceId: number,
  query: DeviceHistoryQuery = {},
  signal?: AbortSignal
): Promise<DeviceHistoryPage> {
  const parts: string[] = [`limit=${query.limit && query.limit > 0 ? query.limit : 100}`];
  if (query.category !== undefined) parts.push(`category=${query.category}`);
  if (query.beforeId !== undefined) parts.push(`beforeId=${query.beforeId}`);
  if (query.search?.trim()) parts.push(`search=${encodeURIComponent(query.search.trim())}`);
  return request<DeviceHistoryPage>(`/api/devices/${deviceId}/history?${parts.join('&')}`, { signal });
}

// ---- The standalone consoles ----

/**
 * Mints the single-use, 60-second ticket the remote and device consoles authenticate with.
 *
 * A separate tab cannot see this app's in-memory token, and a token in a URL would be a long-lived
 * credential in history and screenshots - so the console gets a ticket it immediately exchanges for
 * a normal staff token, passed in the URL FRAGMENT, which is never sent to a server.
 */
export function mintRemoteTicket(deviceId: number): Promise<RemoteTicketResponse> {
  return request<RemoteTicketResponse>('/api/remote/ticket', { method: 'POST', body: { deviceId } });
}

// ---- Discovery, as far as the device page's adopt panel needs it ----

export function getDiscovered(
  customerId: number,
  signal?: AbortSignal
): Promise<DiscoveredDeviceDto[]> {
  return request<DiscoveredDeviceDto[]>(`/api/customers/${customerId}/discovered`, { signal });
}

/**
 * Adopts a discovered peer as a monitored device.
 *
 * The answer carries whether the polling agent was online, which the caller has to say: an adopt
 * that succeeded against an offline agent starts polling on that agent's next connect, and reporting
 * it as "polling now" would have somebody waiting for readings that are not coming yet.
 */
export function adoptDiscovered(
  discoveredId: number,
  req: AdoptSnmpRequest
): Promise<AdoptSnmpResponse> {
  return request<AdoptSnmpResponse>(`/api/discovered/${discoveredId}/adopt`, {
    method: 'POST',
    body: req
  });
}

export function testCustomerSnmp(
  customerId: number,
  req: SnmpTestRequest
): Promise<SnmpTestResult> {
  return request<SnmpTestResult>(`/api/customers/${customerId}/snmp/test`, { method: 'POST', body: req });
}

/**
 * Probes a monitor with what is stored.
 *
 * An empty request is the "use what is stored" reading, which is the question the device page's SNMP
 * tab raises: a machine is in the fleet twice and its SNMP half has gone quiet.
 */
export function testMonitor(
  monitorId: number,
  req: Partial<TestMonitorRequest> = {}
): Promise<MonitorTestResult> {
  return request<MonitorTestResult>(`/api/monitors/${monitorId}/test`, { method: 'POST', body: req });
}

/**
 * Turns a monitor's maintenance mode on or off.
 *
 * The response wraps the monitor alongside whether its polling agent was reachable; only the monitor
 * is returned here, because the caller reads the deadline off the DTO and an agent that is offline
 * still has the deadline replayed to it on its next connect.
 */
export async function setMonitorMaintenance(
  monitorId: number,
  enabled: boolean
): Promise<SnmpMonitorDto> {
  const body = await request<{ monitor: SnmpMonitorDto; agentOnline: boolean }>(
    `/api/monitors/${monitorId}/maintenance`,
    { method: 'POST', body: { enabled } }
  );
  return body.monitor;
}

/** The stored 24-hour window, oldest first. */
export function getMonitorMaintenanceHistory(
  monitorId: number,
  signal?: AbortSignal
): Promise<MetricsReport[]> {
  return request<MetricsReport[]>(`/api/monitors/${monitorId}/maintenance-history`, { signal });
}

// ---------------------------------------------------------------------
//  Expenses
// ---------------------------------------------------------------------

export interface ExpenseFilter {
  /** Incurred bounds as instants; `toUtc` is exclusive. */
  fromUtc?: string;
  toUtc?: string;
  category?: ExpenseCategory;
  customerId?: number;
  search?: string;
  unpaidOnly?: boolean;
}

export function getExpenses(filter: ExpenseFilter, signal?: AbortSignal): Promise<ExpenseListDto> {
  const q = new URLSearchParams();
  if (filter.fromUtc) q.set('from', filter.fromUtc);
  if (filter.toUtc) q.set('to', filter.toUtc);
  if (filter.category !== undefined) q.set('category', String(filter.category));
  if (filter.customerId !== undefined) q.set('customerId', String(filter.customerId));
  if (filter.search?.trim()) q.set('search', filter.search.trim());
  if (filter.unpaidOnly) q.set('unpaidOnly', 'true');
  const query = q.toString();
  return request<ExpenseListDto>('/api/expenses' + (query ? '?' + query : ''), { signal });
}

export function createExpense(req: CreateExpenseRequest): Promise<ExpenseDto> {
  return request<ExpenseDto>('/api/expenses', { method: 'POST', body: req });
}

export function updateExpense(id: number, req: UpdateExpenseRequest): Promise<ExpenseDto> {
  return request<ExpenseDto>(`/api/expenses/${id}`, { method: 'PUT', body: req });
}

export function deleteExpense(id: number): Promise<void> {
  return request<void>(`/api/expenses/${id}`, { method: 'DELETE' });
}

/** Paused templates included: a paused one is still a template somebody may switch back on. */
export function getRecurringExpenses(signal?: AbortSignal): Promise<RecurringExpenseDto[]> {
  return request<RecurringExpenseDto[]>('/api/expenses/recurring?includeInactive=true', { signal });
}

/** Saving a template stamps out any months it already owes, server-side, on the way out. */
export function createRecurringExpense(req: CreateRecurringExpenseRequest): Promise<RecurringExpenseDto> {
  return request<RecurringExpenseDto>('/api/expenses/recurring', { method: 'POST', body: req });
}

export function updateRecurringExpense(id: number, req: UpdateRecurringExpenseRequest): Promise<RecurringExpenseDto> {
  return request<RecurringExpenseDto>(`/api/expenses/recurring/${id}`, { method: 'PUT', body: req });
}

/** Removes the template only; the expenses it already created are kept. */
export function deleteRecurringExpense(id: number): Promise<void> {
  return request<void>(`/api/expenses/recurring/${id}`, { method: 'DELETE' });
}

export function runRecurringExpenses(): Promise<RecurringRunResult> {
  return request<RecurringRunResult>('/api/expenses/recurring/run', { method: 'POST', body: {} });
}

// ---------------------------------------------------------------------
//  Reports
// ---------------------------------------------------------------------

/** `?from=&to=` as instants, `to` exclusive; either may be absent for an open end. */
export function rangeQuery(fromUtc?: string, toUtc?: string): string {
  const q = new URLSearchParams();
  if (fromUtc) q.set('from', fromUtc);
  if (toUtc) q.set('to', toUtc);
  const query = q.toString();
  return query ? '?' + query : '';
}

export function getReportSummary(fromUtc?: string, toUtc?: string, signal?: AbortSignal): Promise<ReportSummaryDto> {
  return request<ReportSummaryDto>('/api/reports/summary' + rangeQuery(fromUtc, toUtc), { signal });
}

export function reportPdfPath(fromUtc?: string, toUtc?: string): string {
  return '/api/reports/summary/pdf' + rangeQuery(fromUtc, toUtc);
}

export function reportCsvPath(fromUtc?: string, toUtc?: string): string {
  return '/api/reports/summary/csv' + rangeQuery(fromUtc, toUtc);
}

// ---------------------------------------------------------------------
//  Profit and loss
// ---------------------------------------------------------------------

/** Grouping always travels; the range is optional and the server defaults it (eight quarters / five years). */
function plQuery(grouping: ProfitLossGrouping, fromUtc?: string, toUtc?: string): string {
  const range = rangeQuery(fromUtc, toUtc);
  return `${range}${range ? '&' : '?'}grouping=${grouping}`;
}

export function getProfitLoss(
  grouping: ProfitLossGrouping,
  fromUtc?: string,
  toUtc?: string,
  signal?: AbortSignal
): Promise<ProfitLossReportDto> {
  return request<ProfitLossReportDto>('/api/reports/profit-loss' + plQuery(grouping, fromUtc, toUtc), { signal });
}

export function profitLossPdfPath(grouping: ProfitLossGrouping, fromUtc?: string, toUtc?: string): string {
  return '/api/reports/profit-loss/pdf' + plQuery(grouping, fromUtc, toUtc);
}

export function profitLossCsvPath(grouping: ProfitLossGrouping, fromUtc?: string, toUtc?: string): string {
  return '/api/reports/profit-loss/csv' + plQuery(grouping, fromUtc, toUtc);
}

// ---------------------------------------------------------------------
//  Security (admin)
// ---------------------------------------------------------------------

export function getLockedAccounts(signal?: AbortSignal): Promise<LockedAccountDto[]> {
  return request<LockedAccountDto[]>('/api/security/lockouts', { signal });
}

/**
 * Releases a lock, and clears its tally so the person gets a full set of attempts back. A POST with the
 * address in the body, not a DELETE with it in the URL: an email address is personal data, and the URL
 * is the part of a request that reaches access logs.
 */
export function unlockAccount(email: string, portal: boolean): Promise<void> {
  return request<void>('/api/security/lockouts/unlock', { method: 'POST', body: { email, portal } });
}

export function getSignInFailures(
  email: string,
  limit: number,
  hours: number,
  signal?: AbortSignal
): Promise<SignInFailureListDto> {
  const q = new URLSearchParams({ limit: String(limit), hours: String(hours) });
  if (email.trim()) q.set('email', email.trim());
  return request<SignInFailureListDto>(`/api/security/sign-in-failures?${q.toString()}`, { signal });
}

export function getSecurityAlerts(signal?: AbortSignal): Promise<SecurityAlertDto> {
  return request<SecurityAlertDto>('/api/security/alerts', { signal });
}

/** Blank switches alerts off; a malformed address is refused with a sentence. */
export function saveSecurityAlerts(notifyEmail: string): Promise<SecurityAlertDto> {
  return request<SecurityAlertDto>('/api/security/alerts', { method: 'PUT', body: { notifyEmail } });
}

// ---------------------------------------------------------------------
//  Audit log (admin)
// ---------------------------------------------------------------------

export interface AuditFilter {
  actorKind?: AuditActorKind;
  actor?: string;
  category?: AuditCategory;
  outcome?: AuditOutcome;
  search?: string;
  fromUtc?: string;
  /** Pages backwards by id: the oldest row on screen. Stable while rows keep arriving. */
  beforeId?: number;
  limit?: number;
}

export function auditQuery(f: AuditFilter): string {
  const q = new URLSearchParams();
  if (f.actorKind !== undefined) q.set('actorKind', String(f.actorKind));
  if (f.actor?.trim()) q.set('actor', f.actor.trim());
  if (f.category !== undefined) q.set('category', String(f.category));
  if (f.outcome !== undefined) q.set('outcome', String(f.outcome));
  if (f.search?.trim()) q.set('search', f.search.trim());
  if (f.fromUtc) q.set('from', f.fromUtc);
  if (f.beforeId !== undefined) q.set('beforeId', String(f.beforeId));
  if (f.limit) q.set('limit', String(f.limit));
  const query = q.toString();
  return query ? '?' + query : '';
}

export function getAudit(filter: AuditFilter, signal?: AbortSignal): Promise<AuditPageDto> {
  return request<AuditPageDto>('/api/audit' + auditQuery(filter), { signal });
}

/** The export is itself recorded in the log, with the filter it was run under. */
export function auditCsvPath(filter: AuditFilter): string {
  return '/api/audit/csv' + auditQuery({ ...filter, beforeId: undefined, limit: undefined });
}

// ---------------------------------------------------------------------
//  Settings and the mail account (read staff, write admin)
// ---------------------------------------------------------------------

export function getSettings(signal?: AbortSignal): Promise<AppSettingsResponse> {
  return request<AppSettingsResponse>('/api/settings', { signal });
}

/** Answers with what was STORED, which the server may have narrowed - re-bind to it. */
export function saveSettings(settings: AppSettingsDto): Promise<AppSettingsResponse> {
  return request<AppSettingsResponse>('/api/settings', { method: 'PUT', body: settings });
}

export function resetSettings(): Promise<AppSettingsResponse> {
  return request<AppSettingsResponse>('/api/settings/reset', { method: 'POST', body: {} });
}

export function getMailAccount(signal?: AbortSignal): Promise<MailAccountDto> {
  return request<MailAccountDto>('/api/mail', { signal });
}

export function updateMailAccount(req: UpdateMailAccountRequest): Promise<MailAccountDto> {
  return request<MailAccountDto>('/api/mail', { method: 'PUT', body: req });
}

/**
 * Probes the form, not the database. NEVER THROWS: a probe that could not run comes back as a failed
 * result, so a panel that said "Testing…" before awaiting can never be left saying it.
 */
export async function testMail(req: MailTestRequest): Promise<MailTestResult> {
  try {
    return await request<MailTestResult>('/api/mail/test', { method: 'POST', body: req });
  } catch (error) {
    const skipped = { checked: false, ok: false, detail: '' };
    return { success: false, smtp: skipped, pop3: skipped, summary: reason(error, 'The test could not be run.') };
  }
}

export function getPayPalAccount(signal?: AbortSignal): Promise<PayPalAccountDto> {
  return request<PayPalAccountDto>('/api/paypal', { signal });
}

export function updatePayPalAccount(req: UpdatePayPalAccountRequest): Promise<PayPalAccountDto> {
  return request<PayPalAccountDto>('/api/paypal', { method: 'PUT', body: req });
}

/** Never throws, for the same reason as `testMail`. */
export async function testPayPal(req: PayPalTestRequest): Promise<PayPalTestResult> {
  try {
    return await request<PayPalTestResult>('/api/paypal/test', { method: 'POST', body: req });
  } catch (error) {
    return { success: false, message: reason(error, 'The test could not be run.') };
  }
}

// ---------------------------------------------------------------------
//  The server's own builds - installed, available, switching and going back
// ---------------------------------------------------------------------

export function getServerUpdateStatus(signal?: AbortSignal): Promise<ServerUpdateStatusDto> {
  return request<ServerUpdateStatusDto>('/api/server-update', { signal });
}

export function saveServerUpdateConfig(req: UpdateServerUpdateConfigRequest): Promise<ServerUpdateConfigDto> {
  return request<ServerUpdateConfigDto>('/api/server-update/config', { method: 'PUT', body: req });
}

export function checkServerUpdates(): Promise<ServerUpdateStatusDto> {
  return request<ServerUpdateStatusDto>('/api/server-update/check', { method: 'POST', body: {} });
}

function failedAction(error: unknown, fallback: string): ServerUpdateActionResult {
  const message = reason(error, fallback);
  return { success: false, summary: message, error: message, restarting: false };
}

/** A refusal is a 200 carrying its reason; a transport failure is folded into the same shape. */
export async function installServerVersion(version: string | null, activate: boolean): Promise<ServerUpdateActionResult> {
  try {
    return await request<ServerUpdateActionResult>('/api/server-update/install', { method: 'POST', body: { version, activate } });
  } catch (error) {
    return failedAction(error, 'The build could not be installed.');
  }
}

export async function activateServerVersion(version: string | null, restoreDatabase: boolean): Promise<ServerUpdateActionResult> {
  try {
    return await request<ServerUpdateActionResult>('/api/server-update/activate', {
      method: 'POST',
      body: { version, restoreDatabase }
    });
  } catch (error) {
    return failedAction(error, 'The server could not be switched.');
  }
}

export async function deleteServerVersion(version: string): Promise<ServerUpdateActionResult> {
  try {
    return await request<ServerUpdateActionResult>(`/api/server-update/versions/${encodeURIComponent(version)}`, {
      method: 'DELETE'
    });
  } catch (error) {
    return failedAction(error, 'The build could not be removed.');
  }
}

/** For `uploadFiles`, which posts the file as `files`. */
export function serverUpdateUploadPath(activate: boolean): string {
  return `/api/server-update/upload?activate=${activate}`;
}

function reason(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message || fallback;
  return `The CRM could not be reached. (${error instanceof Error ? error.message : String(error)})`;
}

// ---------------------------------------------------------------------
//  Billing - one customer's rate rules, time, purchases and the work timer
// ---------------------------------------------------------------------

export function getBillingConfig(customerId: number, signal?: AbortSignal): Promise<BillingConfigDto> {
  return request<BillingConfigDto>(`/api/customers/${customerId}/billing-config`, { signal });
}

export function getTimeEntries(customerId: number, signal?: AbortSignal): Promise<TimeEntryDto[]> {
  return request<TimeEntryDto[]>(`/api/customers/${customerId}/time`, { signal }).then(r => r ?? []);
}

export function getPurchases(customerId: number, signal?: AbortSignal): Promise<PurchaseDto[]> {
  return request<PurchaseDto[]>(`/api/customers/${customerId}/purchases`, { signal }).then(r => r ?? []);
}

/** A refusal names the tickets that have to be resolved first - shown as the server wrote it. */
export function generateInvoice(customerId: number): Promise<InvoiceDto> {
  return request<InvoiceDto>(`/api/customers/${customerId}/invoices`, { method: 'POST', body: {} });
}

/** A customer's monthly auto-billing schedule, and who is emailed when it runs. */
export function getAutoBilling(customerId: number, signal?: AbortSignal): Promise<AutoBillingDto> {
  return request<AutoBillingDto>(`/api/customers/${customerId}/auto-billing`, { signal });
}

/** Admin. A day outside 1-28 is refused with a sentence. */
export function updateAutoBilling(customerId: number, enabled: boolean, dayOfMonth: number): Promise<AutoBillingDto> {
  return request<AutoBillingDto>(`/api/customers/${customerId}/auto-billing`, {
    method: 'PUT',
    body: { enabled, dayOfMonth }
  });
}

/** Admin. Bills now, exactly as the scheduled run would, without using up this month's run. */
export function runAutoBilling(customerId: number): Promise<AutoBillRunResult> {
  return request<AutoBillRunResult>(`/api/customers/${customerId}/auto-billing/run`, { method: 'POST', body: {} });
}

/** The signed-in person's running timer - on any customer - or null; the server answers 204 for none. */
export async function getRunningTimer(signal?: AbortSignal): Promise<WorkTimerDto | null> {
  return (await request<WorkTimerDto | undefined>('/api/timer', { signal })) ?? null;
}

export function startTimer(customerId: number, ticketId: number, notes: string): Promise<WorkTimerDto> {
  return request<WorkTimerDto>('/api/timer/start', { method: 'POST', body: { customerId, ticketId, notes } });
}

/** A blank note keeps the one the timer was started with. */
export function stopTimer(notes: string, billable: boolean): Promise<TimeEntryDto> {
  return request<TimeEntryDto>('/api/timer/stop', { method: 'POST', body: { notes, billable } });
}

export function cancelTimer(): Promise<void> {
  return request<void>('/api/timer', { method: 'DELETE' });
}

export function createTimeEntry(req: CreateTimeEntryRequest): Promise<TimeEntryDto> {
  return request<TimeEntryDto>('/api/time', { method: 'POST', body: req });
}

export function updateTimeEntry(id: number, req: UpdateTimeEntryRequest): Promise<TimeEntryDto> {
  return request<TimeEntryDto>(`/api/time/${id}`, { method: 'PUT', body: req });
}

export function deleteTimeEntry(id: number): Promise<void> {
  return request<void>(`/api/time/${id}`, { method: 'DELETE' });
}

export function createPurchase(req: CreatePurchaseRequest): Promise<PurchaseDto> {
  return request<PurchaseDto>('/api/purchases', { method: 'POST', body: req });
}

export function updatePurchase(id: number, req: UpdatePurchaseRequest): Promise<PurchaseDto> {
  return request<PurchaseDto>(`/api/purchases/${id}`, { method: 'PUT', body: req });
}

export function deletePurchase(id: number): Promise<void> {
  return request<void>(`/api/purchases/${id}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------
//  Network: discovery, deployment, Synology enrollment, and one monitor
// ---------------------------------------------------------------------

export function getMonitor(monitorId: number, signal?: AbortSignal): Promise<SnmpMonitorDto> {
  return request<SnmpMonitorDto>(`/api/monitors/${monitorId}`, { signal });
}

/** Every optional member null = "keep what is stored", so a one-field edit is safe. */
export function updateMonitor(monitorId: number, req: UpdateSnmpMonitorRequest): Promise<SnmpMonitorDto | undefined> {
  return request<SnmpMonitorDto | undefined>(`/api/monitors/${monitorId}`, { method: 'PUT', body: req });
}

export function removeMonitor(monitorId: number): Promise<void> {
  return request<void>(`/api/monitors/${monitorId}`, { method: 'DELETE' });
}

/** Asks an online agent to scan its subnets. Results arrive later, over `DiscoveryChanged`. */
export function scanNetwork(deviceId: number): Promise<void> {
  return request<void>(`/api/devices/${deviceId}/scan`, { method: 'POST', body: {} });
}

/**
 * Push-installs the agent from the on-site agent that found the peer. The admin credentials travel in
 * the body once and are never stored; "started" means the install launched, and the target reports
 * its own outcome into the deployment log.
 */
export function deployAgent(discoveredId: number, adminUsername: string, adminPassword: string): Promise<void> {
  return request<void>(`/api/discovered/${discoveredId}/deploy`, {
    method: 'POST',
    body: { adminUsername, adminPassword }
  });
}

/** A one-time token plus the install command (Windows) or the server URL (the Synology package). */
export function mintDiscoveredEnrollToken(discoveredId: number): Promise<EnrollInfoDto> {
  return request<EnrollInfoDto>(`/api/discovered/${discoveredId}/enroll-token`, { method: 'POST', body: {} });
}

/** Sent by the agent that found the peer - on its network by definition. "Sent", never "woke". */
export function wakeDiscovered(discoveredId: number): Promise<WakeDeviceResponse> {
  return request<WakeDeviceResponse>(`/api/discovered/${discoveredId}/wake`, { method: 'POST', body: {} });
}

export function getDeployLog(customerId: number, signal?: AbortSignal): Promise<DeployLogDto[]> {
  return request<DeployLogDto[]>(`/api/customers/${customerId}/deploy-log`, { signal }).then(r => r ?? []);
}

/** Probes both channels and stores nothing; each channel reports on its own. */
export function testSynology(customerId: number, req: SynologyTestRequest): Promise<SynologyTestResult> {
  return request<SynologyTestResult>(`/api/customers/${customerId}/synology/test`, { method: 'POST', body: req });
}

export function enrollSynology(customerId: number, req: EnrollSynologyRequest): Promise<unknown> {
  return request<unknown>(`/api/customers/${customerId}/synology`, { method: 'POST', body: req });
}

// ---- staff accounts ----

export function getStaffUsers(signal?: AbortSignal): Promise<UserDto[]> {
  return request<UserDto[]>('/api/users', { signal }).then(r => r ?? []);
}

export function createStaffUser(body: CreateStaffUserRequest): Promise<UserDto> {
  return request<UserDto>('/api/users', { method: 'POST', body });
}

export function updateStaffUser(id: number, body: UpdateStaffUserRequest): Promise<UserDto> {
  return request<UserDto>(`/api/users/${id}`, { method: 'PUT', body });
}

export function resetStaffPassword(id: number, body: ResetStaffPasswordRequest): Promise<UserDto> {
  return request<UserDto>(`/api/users/${id}/password`, { method: 'POST', body });
}
